"use client";

import Toast from "@/components/design-system/toast";
import { clearRemovedItems, hideListToast, showListToast } from "@/store/addToShoppingListSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useAddToShoppingListMutation } from "@/store/shoppingListApi";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

const ShoppingListToast = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const showToast = useAppSelector((state) => state.addToShoppingListSlice.showToast);
  const toastMessage = useAppSelector((state) => state.addToShoppingListSlice.toastMessage);
  const toastType = useAppSelector((state) => state.addToShoppingListSlice.toastType);
  const removedMpIds = useAppSelector((state) => state.addToShoppingListSlice.removedMpIds);
  const removedItems = useAppSelector((state) => state.addToShoppingListSlice.removedItems);

  const [addToShoppingList] = useAddToShoppingListMutation();

  const handleUndo = useCallback(async () => {
    if (removedMpIds.length === 0) return;

    // Reverse the order so items are restored in the correct order
    // (last item added first, so the first item ends up at the top)
    const reversedMpIds = [...removedMpIds].reverse();

    try {
      await addToShoppingList(reversedMpIds).unwrap();
      // Refresh the route to show restored items
      router.refresh();
      dispatch(clearRemovedItems());
      dispatch(hideListToast());
    } catch (error) {
      console.error("Failed to undo removal:", error);
      // Show error message but keep removedMpIds intact so user can retry
      dispatch(
        showListToast({
          message: "Failed to restore items. Please try again.",
          type: "remove",
          removedMpIds,
          removedItems,
        }),
      );
    }
  }, [removedMpIds, removedItems, addToShoppingList, dispatch, router]);

  // Remove toast type: show close button and Undo action
  if (toastType === "remove") {
    return (
      <Toast
        toastMessage={toastMessage}
        showToast={showToast}
        toastType={toastType}
        hideToast={hideListToast}
        showOnMobile
        showToastTime={6000}
        toastLocation="top"
        showCloseButton
        onAction={handleUndo}
        actionText="Undo"
      />
    );
  }

  if (toastType === "success") {
    return (
      <Toast
        toastMessage="Item Added to Shopping List"
        toastSubMessage={toastMessage}
        linkText="View Shopping List"
        linkHref="/shopping-list"
        showToast={showToast}
        toastType={toastType}
        hideToast={hideListToast}
        showOnMobile
        showToastTime={6000}
        toastLocation="top"
        showCloseButton
      />
    );
  }

  // Error: plain message toast
  return (
    <Toast
      toastMessage={toastMessage}
      showToast={showToast}
      toastType={toastType}
      hideToast={hideListToast}
      showOnMobile
      showToastTime={6000}
      toastLocation="top"
    />
  );
};

export default ShoppingListToast;
